import { Divider, Grid, Skeleton } from '@mui/material';
import { HSpacer, RoutalPalette, RoutalSearchInput, VSpacer } from '@nx-smartmonkey/ui';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import VehiclesPlaceholder from '../../../assets/img/placeholders/vehicles_placeholder.svg';
import { HeaderContent } from '../../../components/HeaderContent';
import { RouteAvatarName } from '../../../components/RouteAvatarName';
import { TableColumnsSortConfig } from '../../../context/shared/tableColumns';
import { VehiclesTableColumnKeys } from '../../../context/vehicle/tableColumns';
import { Vehicle } from '../../../domain/vehicle/Vehicle';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useRetrieveSupportDocs } from '../../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import { useSearchVehicles } from '../../../hooks/vehicle/useSearchVehicles';
import { useSetSelectedVehicles } from '../../../hooks/vehicle/useSetSelectedVehicles';
import { useSetVehiclesColumns } from '../../../hooks/vehicle/useSetVehiclesColumns';
import Button from '../../../react-components/Button';
import Text from '../../../react-components/Text';
import { DataTable } from '../../../react-components/data-table/DataTable';
import eventsPanel, { PageViewPages } from '../../../services/events';
import { VehiclesActionsMenu } from './VehiclesActionsMenu';
import { VehiclesFromFileDialog } from './VehiclesFromFileDialog';
import { VehiclesFromScratchDialog } from './VehiclesFromScratchDialog';
import { getVehiclesColumns } from './VehiclesListColumns';
import { VehiclesListColumnsButton } from './VehiclesListColumnsButton';
import { VehicleRightPanel } from './vehicle-right-panel/VehicleRightPanel';

export const DriversVehicles = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { detailType, setRightPanelInfo } = useRetrieveRightPanelInfo();
  const { retrieveDoc } = useRetrieveSupportDocs();

  const [dialog, setDialog] = useState<`vehiclesFromScratch` | `vehiclesFromFile` | undefined>(undefined);

  const { data: activeProject } = useRetrieveActiveProject();
  const { data: user } = useRetrieveUser();
  const searchText = searchParams.get(`search_text`) ?? ``;

  const [tableContextualAnchor, setTableContextualAnchor] = useState<
    { screenPosition: { top: number; left: number }; vehicle: Vehicle } | undefined
  >(undefined);

  const { tableColumns } = useSetVehiclesColumns();

  const { data: selectedVehicles, run: setSelectedVehicles } = useSetSelectedVehicles();

  const [sortVehiclesInfo, setSortVehiclesInfo] = useState<TableColumnsSortConfig<VehiclesTableColumnKeys>>({
    orderBy: `created_at`,
    order: `desc`,
  });

  const [searchParameters, setSearchParameters] = useState<{ limit: number; offset: number }>({
    offset: 0,
    limit: 100,
  });

  const { isLoading, data: vehiclesPagination } = useSearchVehicles({
    offset: searchParameters.offset,
    limit: searchParameters.limit,
    sortBy: sortVehiclesInfo.orderBy,
    sortDirection: sortVehiclesInfo.order,
    searchText: searchText !== `` ? searchText : undefined,
  });

  useEffect(() => {
    eventsPanel.pageView(PageViewPages.vehicles);
  }, []);

  const { docs, limit, page, pages, total } = vehiclesPagination || {};

  return (
    <>
      <Grid
        container
        style={{
          height: `100%`,
          width: `100%`,
          zIndex: 2,
          backgroundColor: RoutalPalette.neutral02n,
          display: `flex`,
          flexDirection: `row`,
          minWidth: `1080px`,
        }}
        wrap="nowrap"
      >
        <Grid container direction="column" wrap="nowrap">
          <Grid
            container
            direction="column"
            style={{
              padding: `11px 20px`,
              backgroundColor: `white`,
              borderBottom: `1px solid ${RoutalPalette.neutral10}`,
            }}
            wrap="nowrap"
          >
            <Grid container direction="row" wrap="nowrap">
              <HeaderContent
                titleIntlId="vehicles.title"
                article={{
                  intlId: `vehicles.article`,
                  link: retrieveDoc({ supportPage: `vehicles`, language: user?.language.value }),
                }}
              />

              <Grid container item justifyContent="flex-end">
                <Button
                  onClick={() => {
                    setDialog(`vehiclesFromScratch`);
                  }}
                >
                  <Text style={{ color: `white` }} intlId="plans.create_vehicle_from_scratch"></Text>
                </Button>

                {activeProject?.managed ? (
                  <>
                    <HSpacer medium />

                    <Button
                      styleType="secondary"
                      onClick={() => {
                        setDialog(`vehiclesFromFile`);
                      }}
                    >
                      <Text style={{ color: `white` }} intlId="plans.upload_excel"></Text>
                    </Button>
                  </>
                ) : null}
              </Grid>
            </Grid>
          </Grid>

          <Grid
            container
            direction="row"
            wrap="nowrap"
            style={{ height: `calc(100% - 70px)`, backgroundColor: RoutalPalette.neutral02n }}
          >
            <Grid
              container
              direction="column"
              style={{ padding: `12px 22px`, height: `100%`, width: `100%` }}
              wrap="nowrap"
            >
              {isLoading || (total && total > 0) || (!!searchText && searchText !== ``) ? (
                <>
                  <Grid container>
                    <Grid item container alignItems="center" wrap="nowrap">
                      <Grid container style={{ height: `100%`, width: `100%` }} justifyContent="flex-end">
                        <Grid item>
                          <Grid container direction="column" justifyContent="center" style={{ height: `100%` }}>
                            {isLoading ? (
                              <Skeleton variant="rectangular" animation="pulse" height={36} width={259} />
                            ) : (
                              <RoutalSearchInput
                                inputContainerStyles={{
                                  width: `260px`,
                                }}
                                fieldValue={searchText}
                                onChange={_.debounce((text: any) => {
                                  if (text) {
                                    searchParams.set(`search_text`, text);
                                  } else {
                                    searchParams.delete(`search_text`);
                                  }
                                  setSearchParams(searchParams);
                                }, 300)}
                                searchLive
                              />
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid
                      container
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      wrap="nowrap"
                      style={{
                        minHeight: `46px`,
                        marginBottom: `2px`,
                      }}
                    >
                      <Grid item container alignItems="center">
                        {isLoading ? (
                          <Skeleton variant="rectangular" animation="pulse" height={20} width={220} />
                        ) : (
                          <Text
                            variant="body"
                            intlId={`${
                              (selectedVehicles ?? []).length > 0
                                ? `vehicles.header.selected_info`
                                : `vehicles.header.num_rows`
                            }`}
                            intlValues={{
                              num: (selectedVehicles ?? []).length,
                              total: total ?? ``,
                            }}
                          />
                        )}

                        <HSpacer small />

                        {(selectedVehicles ?? []).length > 0 ? (
                          <VehiclesActionsMenu
                            type="popover"
                            vehicles={(vehiclesPagination?.docs ?? []).filter((doc) =>
                              selectedVehicles.includes(doc.id)
                            )}
                            popoverProps={{
                              hideArrow: false,
                              style: {
                                justifyContent: `space-between`,
                                width: `240px`,
                              },

                              content: <Text variant="body" style={{ cursor: `pointer` }} intlId="vehicles.actions" />,
                            }}
                          />
                        ) : null}
                      </Grid>

                      <Grid item container alignItems="center" justifyContent="flex-end">
                        <VehiclesListColumnsButton id="vehicles-configurable-table-columns" />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Divider />

                  <Grid
                    item
                    container
                    style={{ overflowX: `auto`, overflowY: `hidden`, height: `100% `, width: `100%` }}
                  >
                    {activeProject && (isLoading || (docs ?? []).length > 0) ? (
                      <Grid container style={{ width: `100%`, height: `100%`, position: `relative` }}>
                        <div style={{ height: `100%`, width: `100%`, overflowX: `auto`, position: `absolute` }}>
                          <DataTable
                            loading={isLoading}
                            docs={(docs ?? []).map((driver: Vehicle) => {
                              const checkboxIcon = {
                                element: (
                                  <RouteAvatarName
                                    name={driver.getShortLabel()}
                                    color={driver.color?.value}
                                    additionalStyles={{ width: `20px`, height: `20px` }}
                                  />
                                ),
                              };

                              return {
                                doc: driver,
                                checkboxIcon,
                                disabled: !driver.enabled,
                              };
                            })}
                            limit={limit}
                            page={page}
                            pages={pages}
                            total={total ?? 0}
                            noFooter={isLoading}
                            onRowDoubleClick={(doc) => {
                              setRightPanelInfo({
                                detailType: `vehicle`,
                                detailId: `${doc.id}`,
                                detailTab: null,
                              });
                            }}
                            onChangePage={({ offset, limit }) =>
                              setSearchParameters({
                                ...searchParameters,
                                offset,
                                limit,
                              })
                            }
                            hover
                            handleOnContextMenu={(ev: any, vehicle: Vehicle) => {
                              ev.preventDefault();
                              setTableContextualAnchor({
                                screenPosition: { top: ev.clientY, left: ev.clientX },
                                vehicle,
                              });
                            }}
                            selectedIds={selectedVehicles}
                            onMultipleSelection={({ selectedIds, fromGlobalCheckbox }) => {
                              if (fromGlobalCheckbox) {
                                if (selectedVehicles.length > 0) {
                                  setSelectedVehicles([]);
                                } else {
                                  setSelectedVehicles((docs ?? []).map((fS) => fS.id));
                                }
                              } else {
                                setSelectedVehicles(selectedIds ?? []);
                              }
                            }}
                            sortDirection={sortVehiclesInfo.order}
                            sortBy={sortVehiclesInfo.orderBy}
                            onColumnSort={(column: any, order: `asc` | `desc`) => {
                              setSortVehiclesInfo({ order, orderBy: column });
                            }}
                            columns={[
                              ...getVehiclesColumns({
                                activeProject,
                                tableColumns,
                                onOpenRightPanel: (e, doc) => {
                                  e.stopPropagation();
                                  setRightPanelInfo({
                                    detailType: `vehicle`,
                                    detailId: `${doc.id}`,
                                    detailTab: null,
                                  });
                                },
                              }),
                            ]}
                          />

                          {tableContextualAnchor ? (
                            <VehiclesActionsMenu
                              type="contextual"
                              contextAnchor={tableContextualAnchor}
                              setContextAnchor={() => {
                                setTableContextualAnchor(undefined);
                              }}
                              vehicles={[tableContextualAnchor.vehicle]}
                            />
                          ) : null}
                        </div>
                      </Grid>
                    ) : (
                      <Grid
                        container
                        direction="column"
                        alignItems="center"
                        style={{
                          height: `100%`,
                          padding: `32px`,
                        }}
                      >
                        <Text variant="body-large" intlId="no_results" />
                        {/* <Grid container>
                          <Grid item sm={12}>
                            <Text variant="body-large" intlId="no_results" />
                          </Grid>
                        </Grid> */}
                      </Grid>
                    )}
                  </Grid>
                </>
              ) : (
                <Grid
                  container
                  direction="column"
                  alignItems="center"
                  style={{
                    height: `100%`,
                    // Note: This padding top is of 74px to position the content at the same height of the clients empty placeholder.
                    paddingTop: `74px`,
                  }}
                >
                  <Grid container>
                    <Grid item sm={12}>
                      <Grid container direction="column" justifyContent="center">
                        <Grid container justifyContent="center">
                          <img alt="" src={VehiclesPlaceholder} width="150px" />
                        </Grid>

                        <VSpacer medium />

                        <Grid container direction="column" justifyContent="center" style={{ textAlign: `center` }}>
                          <Text variant="h5" intlId="vehicles.no_vehicles.title" />

                          <VSpacer medium />

                          <Text variant="body-large" intlId="vehicles.no_vehicles.info" />
                        </Grid>

                        <VSpacer medium />

                        <Grid container justifyContent="center">
                          <Text
                            variant="button"
                            intlId="check_out_the_documentation"
                            onClick={() =>
                              window.open(
                                retrieveDoc({ supportPage: `createVehicles`, language: user?.language.value }),
                                `_blank`
                              )
                            }
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
            {detailType === `vehicle` ? <VehicleRightPanel /> : null}
          </Grid>
        </Grid>
      </Grid>

      <VehiclesFromScratchDialog
        open={Boolean(dialog === `vehiclesFromScratch`)}
        handleCloseDialog={(e: any) => {
          e.stopPropagation();
          setDialog(undefined);
        }}
      />

      <VehiclesFromFileDialog
        open={Boolean(dialog === `vehiclesFromFile`)}
        handleCloseDialog={(e: any) => {
          e.stopPropagation();
          setDialog(undefined);
        }}
      />
    </>
  );
};
